import { useEffect, useState } from "react";
import { CaretDown, ListBullets } from "@phosphor-icons/react";

/** Temps de lecture estimé (≈ 200 mots/minute), 0 si le texte est très court. */
export function readingMinutes(content) {
  const words = (content || "").trim().split(/\s+/).filter(Boolean).length;
  if (words < 120) return 0;
  return Math.max(1, Math.round(words / 200));
}

/** Position de lecture (0..1) d'un élément dans la fenêtre : 0 = haut de l'élément, 1 = fin atteinte. */
export function readingRatio(el) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  if (rect.height <= 0) return 0;
  const r = (window.innerHeight * 0.3 - rect.top) / rect.height;
  return Math.min(1, Math.max(0, r));
}

/** Fine barre fixée en haut de l'écran qui indique l'avancement dans la séquence en cours. */
export function ReadingProgressBar({ targetRef, resetKey }) {
  const [ratio, setRatio] = useState(0);
  useEffect(() => {
    const update = () => setRatio(readingRatio(targetRef.current));
    update();
    document.addEventListener("scroll", update, { capture: true, passive: true });
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [targetRef, resetKey]);
  return (
    <div className="pointer-events-none fixed left-0 right-0 top-0 z-[60] h-1 bg-transparent" data-testid="reading-progress" aria-hidden="true">
      <div className="h-full bg-gradient-to-r from-primary to-accent transition-[width] duration-150" style={{ width: `${Math.round(ratio * 100)}%` }} />
    </div>
  );
}

/**
 * Sommaire construit à partir des titres (h2/h3) réellement affichés dans le contenu.
 * variant="inline" : bloc repliable au-dessus du texte (petits écrans) ;
 * variant="side"   : colonne collante à droite (grands écrans).
 */
export function SequenceToc({ containerRef, resetKey, variant = "inline" }) {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const root = containerRef.current;
      if (!root) { setItems([]); return; }
      const hs = Array.from(root.querySelectorAll("h2, h3"));
      hs.forEach((h, i) => { if (!h.id) h.id = `seq-h-${i}`; h.style.scrollMarginTop = "96px"; });
      setItems(hs.map((h) => ({ id: h.id, text: h.textContent || "", level: h.tagName === "H3" ? 3 : 2 })));
      setActiveId(hs[0]?.id || null);
    });
    return () => cancelAnimationFrame(raf);
  }, [containerRef, resetKey]);

  useEffect(() => {
    if (items.length < 3) return undefined;
    const onScroll = () => {
      let current = items[0].id;
      for (const it of items) {
        const el = document.getElementById(it.id);
        if (el && el.getBoundingClientRect().top <= 140) current = it.id;
      }
      setActiveId(current);
    };
    onScroll();
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, true);
  }, [items]);

  if (items.length < 3) return null;
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const list = (
    <ul className="space-y-1">
      {items.map((it) => (
        <li key={it.id}>
          <button
            type="button"
            onClick={() => go(it.id)}
            className={`block w-full rounded px-2 py-1 text-left text-xs leading-snug transition-colors ${it.level === 3 ? "pl-5" : ""} ${
              activeId === it.id ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {it.text}
          </button>
        </li>
      ))}
    </ul>
  );

  if (variant === "side") {
    return (
      <nav className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto" aria-label="Sommaire de la séquence" data-testid="sequence-toc-side">
        <p className="mb-2 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
          <ListBullets size={14} /> Dans cette séquence
        </p>
        {list}
      </nav>
    );
  }
  return (
    <div className="mt-4 rounded-lg border border-border bg-secondary/40" data-testid="sequence-toc-inline">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="flex items-center gap-1.5"><ListBullets size={14} /> Dans cette séquence</span>
        <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-2 pb-2">{list}</div>}
    </div>
  );
}
